import { Box, Paper, IconButton, Tooltip, Typography } from "@mui/material";

import VisibilityIcon from "@mui/icons-material/Visibility";

import { useEffect, useState } from "react";
import { getCommissions } from "../../api/api";
import { useNavigate } from "react-router-dom";

import DataTable from "../../components/datatable/DataTable";
import FilterBar from "../../components/common/FilterBar";

import { GridColDef } from "@mui/x-data-grid";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../store/snackbarSlice";

// ✅ FORMAT
const formatNumber = (num: number) =>
  `₹ ${Number(num || 0).toLocaleString("en-IN")}`;

const Commission = () => {
  const [data, setData] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ================= FETCH =================
  const fetchData = async () => {
    try {
      setLoading(true);

      // 🔥 DEFAULT STATUS
      const params: any = {
        search,
      };

      if (status) {
        params.status = status;
      }

      const res = await getCommissions(params);

      setData(res.data?.data || []);
    } catch {
      dispatch(
        showSnackbar({
          message: "Failed to fetch commissions",

          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(fetchData, 400);

    return () => clearTimeout(delay);
  }, [search, status]);

  // ================= COLUMNS =================

  const columns: GridColDef[] = [
    {
      field: "leadCode",
      headerName: "Lead ID",
      flex: 1,
      renderCell: (params) => {
        const row = params.row;

        return row?.loanId?.leadId?.leadCode || "-";
      },
    },

    {
      field: "applicantName",
      headerName: "Applicant",
      flex: 1,
    },

    {
      field: "loanAmount",
      headerName: "Loan Amount",
      flex: 1,
      renderCell: (params) => formatNumber(params.value),
    },

    {
      field: "bankPercentage",
      headerName: "Commission %",
      flex: 1,
      renderCell: (params) => `${params.value || 0}%`,
    },

    {
      field: "bankAmount",
      headerName: "Commission",
      flex: 1,
      renderCell: (params) => formatNumber(params.value),
    },

    {
      field: "bankGstAmount",
      headerName: "GST",
      flex: 1,
      renderCell: (params) => formatNumber(params.value),
    },

    {
      field: "bankFinal",
      headerName: "Final Received",
      flex: 1,
      renderCell: (params) => formatNumber(params.value),
    },

    {
      field: "brokerAmount",
      headerName: "Broker Expense",
      flex: 1,
      renderCell: (params) => formatNumber(params.value),
    },

    {
      field: "profit",
      headerName: "Profit",
      flex: 1,
      renderCell: (params) => {
        const val = params.value || 0;

        return (
          <Typography
            sx={{
              color: val >= 0 ? "green" : "red",
              fontWeight: 700,
            }}
          >
            {formatNumber(val)}
          </Typography>
        );
      },
    },

    {
      field: "createdAt",
      headerName: "Created",
      flex: 1,
      renderCell: (params) =>
        params.value ? new Date(params.value).toLocaleDateString("en-IN") : "-",
    },

    // ================= ACTIONS =================

    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const loanId = params.row?.loanId?._id || params.row?.loanId;

        return (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
            }}
          >
            {/* VIEW ONLY */}

            <Tooltip title="View Commission">
              <IconButton
                size="small"
                onClick={() => navigate(`/commission/view/${loanId}`)}
                sx={{
                  background: "#eff6ff",
                  color: "#2563eb",
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  // ================= UI =================

  return (
    <Box sx={{ p: 1 }}>
      <FilterBar
        title="Commission Management"
        search={search}
        onSearchChange={setSearch}
        status={status}
        onStatusChange={setStatus}
      />

      <Paper
        sx={{
          mt: 2,
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
          border: "1px solid #e2e8f0",
        }}
      >
        <DataTable rows={data} columns={columns} loading={loading} />
      </Paper>
    </Box>
  );
};

export default Commission;
